import React, { useState, useEffect, useRef, useContext } from "react";
import { NotificationAlertContext } from "../app/NotificationAlertContext";

// Persists across remounts so we never replay an already-handled notification
let lastHandledNotificationId = null;

const LEGACY_OBJECT_ID_PATTERN =
  /new\s+ObjectId\(\s*(["'`])([^"'`]*)\1\s*\)/g;
const LEGACY_OBJECT_KEY_PATTERN =
  /([{,]\s*)([A-Za-z_$][A-Za-z0-9_$]*)(\s*:)/g;
const LEGACY_SINGLE_QUOTED_STRING_PATTERN = /'((?:\\.|[^'\\])*)'/g;

const findFirstObjectLiteral = (message) => {
  let start = -1;
  let depth = 0;
  let quote = null;
  let escaped = false;

  for (let index = 0; index < message.length; index += 1) {
    const character = message[index];

    if (start === -1) {
      if (character === "{") {
        start = index;
        depth = 1;
      }
      continue;
    }

    if (quote) {
      if (escaped) {
        escaped = false;
      } else if (character === "\\") {
        escaped = true;
      } else if (character === quote) {
        quote = null;
      }
      continue;
    }

    if (character === '"' || character === "'" || character === "`") {
      quote = character;
      continue;
    }

    if (character === "{") {
      depth += 1;
      continue;
    }

    if (character === "}" && depth > 0) {
      depth -= 1;
      if (depth === 0) {
        return {
          literal: message.slice(start, index + 1),
          start,
          end: index + 1,
        };
      }
    }
  }

  return null;
};

const decodeLegacySingleQuotedString = (value) => {
  let decoded = "";

  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    const nextCharacter = value[index + 1];

    if (
      character === "\\" &&
      (nextCharacter === "'" || nextCharacter === "\\")
    ) {
      decoded += nextCharacter;
      index += 1;
    } else {
      decoded += character;
    }
  }

  return JSON.stringify(decoded);
};

const parseNotificationObject = (literal) => {
  const parse = (value) => {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : null;
    } catch {
      return null;
    }
  };

  const strictObject = parse(literal);
  if (strictObject) return strictObject;

  const normalizedLegacyObject = literal
    .replace(LEGACY_OBJECT_ID_PATTERN, (_match, _quote, id) =>
      JSON.stringify(id)
    )
    .replace(LEGACY_OBJECT_KEY_PATTERN, '$1"$2"$3')
    .replace(
      LEGACY_SINGLE_QUOTED_STRING_PATTERN,
      (_match, value) => decodeLegacySingleQuotedString(value)
    );

  return parse(normalizedLegacyObject);
};

const formatObjectEntries = (object) =>
  Object.entries(object)
    .filter(([key]) => key !== "_id")
    .map(([key, value]) => {
      const formattedValue =
        value && typeof value === "object"
          ? JSON.stringify(value)
          : String(value);
      return `- ${key}: ${formattedValue}`;
    })
    .join("\n");

export const formatNotificationMessage = (message) => {
  if (!message) return "";

  if (typeof message === "object") {
    try {
      return JSON.stringify(message, null, 2);
    } catch {
      return String(message);
    }
  }

  const stringMessage = String(message);
  if (stringMessage.includes("[object Object]")) return "Details available";

  const objectLiteral = findFirstObjectLiteral(stringMessage);
  if (!objectLiteral) return stringMessage;

  const parsedObject = parseNotificationObject(objectLiteral.literal);
  if (!parsedObject) return stringMessage;

  const formattedEntries = formatObjectEntries(parsedObject);
  if (!formattedEntries) return stringMessage;

  return `${stringMessage.slice(
    0,
    objectLiteral.start
  )}\n${formattedEntries}\n${stringMessage.slice(objectLiteral.end)}`;
};

export default function NotificationBell({ fetchNotificationsApi }) {
  const [showPopup, setShowPopup] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [hasNew, setHasNew] = useState(false);
  const audioRef = useRef(null);
  const [toasts, setToasts] = useState([]); // Array of { id, subject, message, visible }
  const toastIdRef = useRef(0);
  const { setNotificationAlert, newNotification } = useContext(
    NotificationAlertContext
  );

  // Fetch notifications when popup opens
  useEffect(() => {
    if (showPopup) {
      fetchNotificationsApi().then((data) => {
        setNotifications(data);
        setHasNew(false); // Clear red dot when viewing
      });
    }
  }, [showPopup, fetchNotificationsApi]);

  // Listen to new notifications from context
  useEffect(() => {
    if (newNotification) {
      const notifId = newNotification._id || newNotification.id;
      if (notifId && notifId === lastHandledNotificationId) return;
      lastHandledNotificationId = notifId || null;
      setHasNew(true);
      setNotifications((prev) => [newNotification, ...prev]);
      if (audioRef.current) {
        audioRef.current.play();
      }
      // Add new toast
      const id = toastIdRef.current++;
      setToasts((prev) => [
        ...prev,
        {
          id,
          subject:
            newNotification.notificationSubject ||
            newNotification.subject ||
            "Notification",
          message: formatNotificationMessage(
            newNotification.notificationMessage || newNotification.message
          ),
          visible: true,
        },
      ]);
      // Auto-hide this toast after 7 seconds (slide out first)
      setTimeout(() => {
        setToasts((prev) =>
          prev.map((t) => (t.id === id ? { ...t, visible: false } : t))
        );
        // Remove from DOM after slide-out (0.5s)
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 500);
      }, 7000);
      // Set sidebar notification alert and vibrate
      setNotificationAlert(true);
      if (navigator.vibrate) {
        navigator.vibrate(5000);
      }
    }
  }, [newNotification, setNotificationAlert]);

  const notificationSound = "/assets/notifications.wav";

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      {/* Toast Notifications Stack */}
      <div style={{
        position: 'fixed',
        top: 24,
        right: 24,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        alignItems: 'flex-end',
        pointerEvents: 'none',
      }}>
        {toasts.map((toast) => (
          <div
            key={toast.id}
            style={{
              minWidth: 320,
              maxWidth: 420,
              background: '#fff',
              border: '2px solid #F75E00',
              borderRadius: 10,
              boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
              padding: '18px 28px 18px 22px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              pointerEvents: 'auto',
              transition: 'transform 0.5s, opacity 0.5s',
              transform: toast.visible ? 'translateX(0)' : 'translateX(120%)',
              opacity: toast.visible ? 1 : 0,
            }}
          >
            <div style={{ fontWeight: 700, color: '#F75E00', fontSize: 17, marginBottom: 2 }}>{toast.subject}</div>
            <div style={{ fontSize: 15, color: '#333', whiteSpace: 'pre-wrap' }}>{toast.message}</div>
          </div>
        ))}
      </div>
      <button
        onClick={() => setShowPopup((v) => !v)}
        style={{
          position: "relative",
          background: "none",
          border: "2px solid #F75E00",
          borderRadius: "50%",
          width: 40,
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          outline: "none",
        }}
        aria-label="Notifications"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F75E00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
        </svg>
        {hasNew && (
          <span
            style={{
              position: "absolute",
              top: 6,
              right: 6,
              width: 10,
              height: 10,
              background: "#F75E00",
              borderRadius: "50%",
              border: "2px solid #fff",
            }}
          />
        )}
      </button>
      <audio ref={audioRef} src={notificationSound} />
      {showPopup && (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: "110%",
            background: "#fff",
            border: "1.5px solid #F75E00",
            borderRadius: 8,
            width: 450,
            maxHeight: 600,
            overflowY: "auto",
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            zIndex: 100,
            padding: "0.5rem 0",
          }}
        >
          <h4 style={{ margin: "10px 0 10px 20px", color: "#F75E00", fontWeight: 700 }}>Notifications</h4>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {(() => {
              // Get today's date in YYYY-MM-DD
              const today = new Date();
              const yyyy = today.getFullYear();
              const mm = String(today.getMonth() + 1).padStart(2, '0');
              const dd = String(today.getDate()).padStart(2, '0');
              const todayStr = `${yyyy}-${mm}-${dd}`;

              // Filter notifications for today
              const todaysNotifications = Array.isArray(notifications)
                ? notifications.filter(n => {
                    if (!n.createdAt) return false;
                    const notifDate = new Date(n.createdAt);
                    const notifStr = `${notifDate.getFullYear()}-${String(notifDate.getMonth() + 1).padStart(2, '0')}-${String(notifDate.getDate()).padStart(2, '0')}`;
                    return notifStr === todayStr;
                  })
                : [];

              if (todaysNotifications.length === 0) {
                return <li style={{ padding: "16px", color: "#888" }}>No notifications</li>;
              }
              return todaysNotifications.map((n, i) => (
                <li
                  key={n._id || i}
                  style={{
                    margin: "0 0 8px 0",
                    padding: "12px 20px",
                    borderBottom: "1px solid #f3f3f3",
                  }}
                >
                  <div style={{ fontWeight: 700, color: "#222", marginBottom: 2 }}>
                    {n.notificationSubject || n.subject || "Notification"}
                  </div>
                  <div style={{ fontSize: 14, color: "#444", whiteSpace: "pre-wrap" }}>
                    {formatNotificationMessage(n.notificationMessage || n.message)}
                  </div>
                  <div style={{ fontSize: 11, color: "#F75E00", marginTop: 4 }}>
                    {n.createdAt
                      ? new Date(n.createdAt).toLocaleString("en-GB", { timeZone: "Europe/London" })
                      : ""}
                  </div>
                </li>
              ));
            })()}
          </ul>
        </div>
      )}
    </div>
  );
}
